import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    CircularProgress,
    OutlinedInput,
    FormHelperText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTeamsTeamMembersPartialUpdateMutation, useServicesSalonListQuery, useSalonsMySalonsRetrieveQuery, useTeamsTeamsListQuery } from '../../../../services/GLOWClient';
// import { useTeamsTeamsSalonTeamsRetrieveQuery } from '../../../../services/GLOWClient'; // TODO: Endpoint not generated
import theme from '../../../../theme';
import { getLocalizedField } from '../../../../utils/localizedFields';

const UpdateMember = ({ open, onClose, member, salons }) => {
    const { i18n } = useTranslation();
    
    // State for form data
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        team_ids: [], // Changed from team to team_ids for multi-select
        service_ids: [],
        salon_id: null
    });
    
    // Fetch salons data for the dropdown using my_salons endpoint
    const { data: salonsData, isLoading: salonsLoading } = useSalonsMySalonsRetrieveQuery();
    
    // Fetch services data for the selected salon
    const { data: servicesData, isLoading: servicesLoading, isFetching: servicesFetching, refetch: refetchServices } = useServicesSalonListQuery(
        formData.salon_id ? { salonId: formData.salon_id, language: i18n.language } : { salonId: '', language: i18n.language },
        {
            skip: !formData.salon_id,
            refetchOnMountOrArgChange: true
        }
    );

    React.useEffect(() => {
        if (open && formData.salon_id) {
            refetchServices();
        }
    }, [open, formData.salon_id, refetchServices]);
    
    // Make sure services is always an array
    const servicesArray = Array.isArray(servicesData) ? servicesData : [];
    
    const [errors, setErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateMember] = useTeamsTeamMembersPartialUpdateMutation();
    
    // Fetch teams (list endpoint) then filter client-side by salon.
    // We fetch even if salon_id is missing so we can still display team names in renderValue.
    const {
        data: teamsData,
        isLoading: teamsLoading,
        refetch: refetchTeams
    } = useTeamsTeamsListQuery(undefined, { skip: !open });
    
    // Refetch teams when dialog opens to ensure fresh data
    React.useEffect(() => {
        if (open) {
            refetchTeams();
        }
    }, [open, refetchTeams]);
    
    // Process teams data - useTeamsTeamsSalonTeamsRetrieveQuery returns teams already filtered by salon
    const filteredTeams = React.useMemo(() => {
        if (!teamsData) return [];

        const teamsArray = Array.isArray(teamsData)
            ? teamsData
            : (teamsData.results ? teamsData.results : []);

        const salonIdNum = typeof formData.salon_id === 'number' ? formData.salon_id : Number(formData.salon_id);
        if (!Number.isFinite(salonIdNum)) return teamsArray;

        return teamsArray.filter((t) => {
            const teamSalonId = typeof t?.salon === 'string' ? parseInt(t.salon, 10) : t?.salon;
            const teamSalonIdNum = typeof teamSalonId === 'number' ? teamSalonId : Number(teamSalonId);
            return Number.isFinite(teamSalonIdNum) && teamSalonIdNum === salonIdNum;
        });
    }, [teamsData, formData.salon_id]);

    // Initialize form data when member or open changes
    useEffect(() => {
        if (member && open) {
            // Extract data from the member object based on API structure
            const serviceIds = member.service_ids || [];
            
            // Get salon ID from team if available
            let salonId = null;
            if (member.salon_id) {
                salonId = member.salon_id;
            } else if (member.team_details && member.team_details.salon_id) {
                salonId = member.team_details.salon_id;
            }
            
            // Set form data with values from the member object
            setFormData({
                first_name: member.user_details?.first_name || '',
                last_name: member.user_details?.last_name || '',
                email: member.user_details?.email || '',
                phone: member.user_details?.phone || '',
                // Use team_ids array or convert single team to array if needed
                team_ids: member.team_ids || (member.team ? [member.team] : []),
                service_ids: serviceIds,
                salon_id: salonId
            });
            setErrors({});
        }
    }, [member, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleServiceChange = (e) => {
        setFormData({
            ...formData,
            service_ids: e.target.value
        });
    };

    // Validate form fields before submission
    const validateForm = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[+]?[0-9]{1,15}$/;
        
        if (!formData.email) {
            newErrors.email = 'L\'email est requis';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Format d\'email invalide';
        }
        
        if (!formData.first_name) {
            newErrors.first_name = 'Le prénom est requis';
        }
        
        if (!formData.last_name) {
            newErrors.last_name = 'Le nom est requis';
        }
        
        if (formData.phone && !phoneRegex.test(formData.phone)) {
            newErrors.phone = 'Numéro de téléphone invalide (chiffres uniquement avec éventuellement un + au début)';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }
        
        setIsUpdating(true);
        try {
            // Prepare the update data
            const updateData = {
                teamMemberId: Number(member.team_member_id), // Ensure it's a number
                patchedTeamMemberRequest: {
                    email: formData.email,
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    phone: formData.phone || null,
                    service_ids: formData.service_ids,
                    team_ids: formData.team_ids.length > 0 ? formData.team_ids : undefined, // Use team_ids array
                    salon: formData.salon_id || undefined // Use salon field to match backend API
                }
            };
            
         
            try {
                // Call the API to update the team member
                const result = await updateMember(updateData).unwrap();
            } catch (apiError) {
                console.error('API error details:', apiError);
                throw apiError; // Re-throw to be caught by the outer catch block
            }
            
            // Refetch teams to get updated data
            if (formData.salon_id) {
                refetchTeams();
            }
            
            // Close the dialog and refresh the data
            onClose(true);
        } catch (error) {
            console.error('Failed to update team member:', error);
            
            // Handle API validation errors
            if (error.data) {
                const apiErrors = {};
                
                // Map API error messages to form fields
                Object.entries(error.data).forEach(([key, value]) => {
                    apiErrors[key] = Array.isArray(value) ? value.join(' ') : value;
                });
                
                setErrors(apiErrors);
            }
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
                Modifier l'employé
            </DialogTitle>
            <DialogContent>
                {errors.submit && (
                    <div style={{ color: theme.colors.error.main, marginBottom: '16px' }}>
                        {errors.submit}
                    </div>
                )}
                
                <Typography variant="h6" gutterBottom>
                    Informations de l'employé
                </Typography>
                
                <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                        // Always update the form data first
                        const { name, value } = e.target;
                        setFormData(prev => ({
                            ...prev,
                            [name]: value
                        }));
                        
                        // Then validate email
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (value && !emailRegex.test(value)) {
                            setErrors(prev => ({
                                ...prev,
                                email: 'Adresse email invalide'
                            }));
                        } else if (errors.email) {
                            // Clear error if valid
                            setErrors(prev => {
                                const newErrors = {...prev};
                                delete newErrors.email;
                                return newErrors;
                            });
                        }
                    }}
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={isUpdating}
                    inputProps={{
                        inputMode: 'email'
                    }}
                />
                
                <div style={{ display: 'flex', gap: '16px' }}>
                    <TextField
                        fullWidth
                        label="Prénom"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.first_name}
                        helperText={errors.first_name}
                        disabled={isUpdating}
                    />
                    
                    <TextField
                        fullWidth
                        label="Nom"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        margin="normal"
                        error={!!errors.last_name}
                        helperText={errors.last_name}
                        disabled={isUpdating}
                    />
                </div>
                
                <TextField
                    fullWidth
                    label="Téléphone (optionnel)"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                        // Always update the form data first
                        const { name, value } = e.target;
                        
                        // Only allow valid phone characters
                        if (value && !/^[+]?[0-9]*$/.test(value)) {
                            // Invalid character - don't update form data
                            setErrors(prev => ({
                                ...prev,
                                phone: 'Numéro de téléphone invalide (chiffres uniquement avec éventuellement un + au début)'
                            }));
                            return;
                        }
                        
                        // Update form data with valid input
                        setFormData(prev => ({
                            ...prev,
                            [name]: value
                        }));
                        
                        // Clear error if it exists
                        if (errors.phone) {
                            setErrors(prev => {
                                const newErrors = {...prev};
                                delete newErrors.phone;
                                return newErrors;
                            });
                        }
                    }}
                    margin="normal"
                    error={!!errors.phone}
                    helperText={errors.phone}
                    disabled={isUpdating}
                    inputProps={{
                        pattern: '[+]?[0-9]{1,15}',
                        inputMode: 'tel'
                    }}
                />
                
                {/* Salon selection dropdown */}
                {/* <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.salon_id}
                    disabled={isUpdating || salonsLoading}
                >
                    <InputLabel>Salon</InputLabel>
                    <Select
                        name="salon_id"
                        value={formData.salon_id || ''}
                        onChange={(e) => {
                            const newSalonId = e.target.value;
                            // Reset team selection when salon changes
                            setFormData({
                                ...formData,
                                salon_id: newSalonId,
                                team_ids: []
                            });
                            
                            // Clear error when field is edited
                            if (errors.salon_id) {
                                setErrors({
                                    ...errors,
                                    salon_id: ''
                                });
                            }
                        }}
                        label="Salon"
                    >
                        <MenuItem value="">
                            <em>Sélectionnez un salon</em>
                        </MenuItem>
                        {salonsLoading ? (
                            <MenuItem disabled>Chargement des salons...</MenuItem>
                        ) : !salonsData || salonsData.length === 0 ? (
                            <MenuItem disabled>Aucun salon disponible</MenuItem>
                        ) : (
                            salonsData.map((salon) => (
                                <MenuItem key={salon.salon_id} value={salon.salon_id}>
                                    {salon.name}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                    {errors.salon_id && <FormHelperText>{errors.salon_id}</FormHelperText>}
                </FormControl> */}
                
                {/* Teams multi-select dropdown */}
                <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.team_ids}
                    disabled={isUpdating || teamsLoading}
                    sx={{ minWidth: '100%' }}
                >
                    <InputLabel id="teams-label">Équipes (optionnel)</InputLabel>
                    <Select
                        labelId="teams-label"
                        name="team_ids"
                        multiple
                        value={formData.team_ids || []}
                        onChange={(e) => {
                            setFormData({
                                ...formData,
                                team_ids: e.target.value
                            });
                            
                            // Clear error when field is edited
                            if (errors.team_ids) {
                                setErrors({
                                    ...errors,
                                    team_ids: ''
                                });
                            }
                        }}
                        input={<OutlinedInput label="Équipes (optionnel)" />}
                        renderValue={(selected) => (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {selected.map((value) => {
                                    const team = filteredTeams?.find(t => String(t.team_id ?? t.id) === String(value));
                                    return (
                                        <Chip 
                                            key={value} 
                                            label={team ? team.name : value} 
                                            size="small" 
                                        />
                                    );
                                })}
                            </div>
                        )}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                    width: 'auto',
                                    minWidth: 250
                                },
                            },
                        }}
                    >
                        {teamsLoading ? (
                            <MenuItem disabled>Chargement des équipes...</MenuItem>
                        ) : !filteredTeams || filteredTeams.length === 0 ? (
                            <MenuItem disabled>Aucune équipe disponible pour ce salon</MenuItem>
                        ) : (
                            filteredTeams.map((team) => (
                                <MenuItem key={team.team_id} value={team.team_id}>
                                    {team.name}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                    {errors.team_ids && <FormHelperText>{errors.team_ids}</FormHelperText>}
                </FormControl>
                
                <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.service_ids}
                    disabled={isUpdating || servicesLoading || servicesFetching}
                    sx={{ minWidth: '100%' }}
                >
                    <InputLabel id="services-label">Services</InputLabel>
                    <Select
                        labelId="services-label"
                        name="service_ids"
                        multiple
                        value={formData.service_ids || []}
                        onChange={handleServiceChange}
                        input={<OutlinedInput label="Services" />}
                        renderValue={(selected) => (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {selected.map((value) => {
                                    const service = servicesArray.find(s => s.service_id === value);
                                    return (
                                        <Chip 
                                            key={value} 
                                            label={service ? getLocalizedField(service, 'name', i18n.language) : `Service ${value}`} 
                                            size="small" 
                                        />
                                    );
                                })}
                            </div>
                        )}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                    width: 'auto',
                                    minWidth: 250
                                },
                            },
                        }}
                    >
                        {servicesLoading || servicesFetching ? (
                            <MenuItem disabled>Chargement des prestations...</MenuItem>
                        ) : servicesArray.length === 0 ? (
                            <MenuItem disabled>Aucun service disponible</MenuItem>
                        ) : (
                            servicesArray.map((service) => (
                                <MenuItem key={service.service_id} value={service.service_id}>
                                    {getLocalizedField(service, 'name', i18n.language)}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                    {errors.service_ids && <FormHelperText>{errors.service_ids}</FormHelperText>}
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} disabled={isUpdating}>
                    Annuler
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary" 
                    disabled={isUpdating}
                >
                    {isUpdating ? <CircularProgress size={24} /> : 'Mettre à jour'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateMember;
